import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, FormControlLabel, IconButton, InputAdornment, Switch, TextField, Typography, useTheme } from '@mui/material';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import dayjs from 'dayjs';
import { SocketContext } from '../socket';
import { voiceApi, errorMessage } from './voiceApi';
import { useVoice } from './VoiceContext';
import {
    formatDuration, formatPhone, personName, whomText, canOpenCall, callMatchesWords, matchingTurns, highlightParts, queryTerms, teacherDialNumber,
    CALL_STATUS_TEXT, statusColor,
} from './callFormat';
import CallDetailDialog from './CallDetailDialog';

const LABEL_TEXT = { student: 'student', parent: 'parent', parent_2: 'parent (2nd)', other: 'other', teacher: 'teacher' };
const TRANSCRIPT_TEXT = { PENDING: 'transcript pending', PROCESSING: 'transcribing', COMPLETED: 'transcript', FAILED: 'transcript failed', SKIPPED: 'no transcript' };
const TRANSCRIPT_COLOR = { COMPLETED: 'success', PROCESSING: 'info', FAILED: 'error' };

// Matched words in a moment: the amber mark from the design mockup, in both themes.
const MARK_SX = {
    light: { backgroundColor: '#f6dc8a', color: '#3d2f00' },
    dark: { backgroundColor: '#8a6d12', color: '#fff3c4' },
};
// The Ask AI answer card: a soft blue panel, as in the design mockup, in both themes.
const ANSWER_COLORS = {
    light: { bg: '#e8f0fb', border: '#1f5fa8', ink: '#1f5fa8', chipBg: '#ffffff', text: '#1c1c1c', muted: '#5f6368' },
    dark: { bg: '#1c2a3d', border: '#7fb0f0', ink: '#7fb0f0', chipBg: '#1e1f22', text: '#ececea', muted: '#a5a8ad' },
};

/** A matched moment: speaker chip, play-from-here chip, the text with the search words marked. */
const Moment = ({ m, terms, markSx, onPlay, contactName = 'Parent' }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, alignItems: 'start', mt: 0.75 }}>
        <Box sx={{ display: 'grid', gap: 0.5, justifyItems: 'center' }}>
            <Chip size="small" label={m.speaker === 'admin' ? 'Admin' : contactName} color={m.speaker === 'admin' ? 'primary' : 'default'} variant={m.speaker === 'admin' ? 'filled' : 'outlined'} />
            {m.start != null && (
                <Chip size="small" variant="outlined" color="primary" icon={<PlayArrowIcon sx={{ fontSize: 14 }} />} label={formatDuration(m.start)} onClick={(e) => { e.stopPropagation(); onPlay(m.start); }} title="Play the recording from here" />
            )}
        </Box>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
            {highlightParts(m.text, terms).map((p, i) => (p.hit
                ? <Box component="mark" key={i} sx={{ ...markSx, px: 0.25, borderRadius: 0.5 }}>{p.text}</Box>
                : <React.Fragment key={i}>{p.text}</React.Fragment>))}
        </Typography>
    </Box>
);

/**
 * "Calls" tab in the student or teacher modal (`student` or `teacher` prop, plan §5.11): that person's
 * calls, newest first, for permitted admins, with a
 * search row above the list (plan §5.9): exact words filter the list instantly from the rows already
 * here; otherwise Search asks the server for the moments that match by meaning, and Ask AI adds a
 * short answer with citations. A moment's time chip opens the call playing from that second.
 */
const CallHistoryTab = ({ student, teacher }) => {
    const kind = teacher ? 'teacher' : 'student';
    const person = teacher || student;
    const contactName = kind === 'teacher' ? 'Teacher' : 'Parent';
    const voice = useVoice();
    const socket = useContext(SocketContext);
    const theme = useTheme();
    const answerColors = ANSWER_COLORS[theme.palette.mode === 'dark' ? 'dark' : 'light'];
    const markSx = MARK_SX[theme.palette.mode === 'dark' ? 'dark' : 'light'];
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [openSeek, setOpenSeek] = useState(null);
    const [q, setQ] = useState('');
    const [exact, setExact] = useState(false);
    const [searching, setSearching] = useState(false);
    const [result, setResult] = useState(null);   // { q, hits, searched_calls, pending, answer } | null
    const [searchError, setSearchError] = useState(null);
    const permitted = Boolean(voice?.permitted);
    const term = q.trim();

    const exactCalls = useMemo(() => (exact && term ? calls.filter((c) => callMatchesWords(c, term)) : calls), [calls, exact, term]);

    const runSearch = async (withAnswer) => {
        if (!term || !person?.uuid) return;
        setSearching(true);
        setSearchError(null);
        try {
            const data = await voiceApi.searchCalls({ kind, uuid: person.uuid }, { q: term, answer: Boolean(withAnswer) });
            setResult({ q: term, ...data });
        } catch (err) {
            setSearchError(errorMessage(err));
        } finally {
            setSearching(false);
        }
    };
    const clearSearch = () => { setQ(''); setResult(null); setSearchError(null); };
    const openAt = (id, seconds) => { setOpenSeek(seconds ?? null); setOpenId(id); };

    const load = useCallback(async () => {
        if (!person?.uuid || !permitted) return;
        setLoading(true);
        try {
            const { call_logs } = await voiceApi.listCalls({ [kind === 'teacher' ? 'teacher_uuid' : 'student_uuid']: person.uuid, limit: 100 });
            setCalls(call_logs);
            setError(null);
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [person?.uuid, kind, permitted]);

    useEffect(() => { load(); }, [load]);

    // Live: reload when a card for this person changes (only while the shared socket is connected).
    useEffect(() => {
        if (!socket || !person?.uuid) return undefined;
        const onCard = (card) => {
            if (card?.student?.uuid === person.uuid || card?.teacher?.uuid === person.uuid || (card?.extra_students || []).some((s) => s.uuid === person.uuid)) load();
        };
        socket.on('voice.activity', onCard);
        return () => socket.off('voice.activity', onCard);
    }, [socket, person?.uuid, load]);

    if (!permitted) {
        return <Typography variant="body2" color="text.secondary">Call recordings and transcripts are visible to admins with the call permission.</Typography>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>Calls</Typography>
                {voice?.canCall && teacher && teacherDialNumber(teacher) && (
                    <Button size="small" startIcon={<PhoneOutlinedIcon />} onClick={() => voice.openDialer({ to_number: teacherDialNumber(teacher), teacher, contact_label: 'teacher' })}>
                        Call teacher
                    </Button>
                )}
                {voice?.canCall && student?.parent_phone && (
                    <Button size="small" startIcon={<PhoneOutlinedIcon />} onClick={() => voice.openDialer({ to_number: student.parent_phone, student, contact_label: 'parent' })}>
                        Call parent
                    </Button>
                )}
                {voice?.canCall && student?.phone && (
                    <Button size="small" startIcon={<PhoneOutlinedIcon />} onClick={() => voice.openDialer({ to_number: student.phone, student, contact_label: 'student' })}>
                        Call student
                    </Button>
                )}
                {loading && <CircularProgress size={16} />}
            </Box>
            {error && <Typography variant="body2" color="error">{error}</Typography>}

            {/* search row */}
            {calls.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    <TextField
                        value={q}
                        onChange={(e) => { setQ(e.target.value); if (result) setResult(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !exact) runSearch(false); }}
                        placeholder={kind === 'teacher' ? "Search this teacher's calls… e.g. 대강, 교재, 급여" : "Search this student's calls… e.g. 환불, ISEF, 시험 리셋"}
                        size="small"
                        sx={{ flex: '1 1 320px' }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                            endAdornment: q ? <InputAdornment position="end"><IconButton size="small" onClick={clearSearch} aria-label="Clear search"><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null,
                        }}
                    />
                    <Button variant="contained" size="small" onClick={() => runSearch(false)} disabled={exact || !term || searching}>Search</Button>
                    <Button variant="outlined" size="small" onClick={() => runSearch(true)} disabled={!term || searching}>Ask AI</Button>
                    {searching && <CircularProgress size={16} />}
                </Box>
            )}
            {/* the exact-words switch lives with the hint that explains it, under the field */}
            {calls.length > 0 && !result && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mt: -1, mb: 1.5 }}>
                    <FormControlLabel
                        control={<Switch size="small" checked={exact} onChange={(e) => { setExact(e.target.checked); setResult(null); }} />}
                        label={<Typography variant="caption" color={exact ? 'text.primary' : 'text.secondary'}>Exact words</Typography>}
                        sx={{ mr: 0, ml: -0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>
                        {exact ? 'The list filters as you type. Turn off to search by meaning.' : 'Search finds the meaning, in Korean or English. Turn on to filter the list by the letters you type.'}
                    </Typography>
                </Box>
            )}
            {searchError && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{searchError}</Typography>}

            {/* semantic results replace the list */}
            {result ? (
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {result.hits.length ? `${result.hits.length} of ${result.searched_calls} calls match “${result.q}”` : `No call matches “${result.q}” · ${result.searched_calls} searched`}
                        {result.pending ? ` · ${result.pending} older calls not indexed yet — search again in a moment` : ''}
                        {' · '}<Button size="small" onClick={clearSearch} sx={{ textTransform: 'none', py: 0, minWidth: 0 }}>Clear</Button>
                    </Typography>
                    {result.answer && (
                        <Box sx={{ border: '1px solid', borderColor: answerColors.border, backgroundColor: answerColors.bg, color: answerColors.text, borderRadius: 2, p: 1.5, mb: 1.5 }}>
                            <Typography variant="caption" sx={{ color: answerColors.ink, fontWeight: 700, letterSpacing: 1 }}>ASK AI · ANSWER</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.65, whiteSpace: 'pre-wrap', color: answerColors.text }}>
                                {result.answer.found ? result.answer.answer : (result.answer.answer || `Nothing in this ${kind}\u2019s calls covers that.`)}
                            </Typography>
                            {result.answer.citations?.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
                                    {result.answer.citations.map((cid) => {
                                        const hit = result.hits.find((h) => h.matches.some((m) => m.chunk_id === cid));
                                        const m = hit?.matches.find((x) => x.chunk_id === cid);
                                        if (!hit || !m) return null;
                                        const when = dayjs(hit.call.started_at || hit.call.createdAt).format('h:mm A');
                                        return (
                                            <Chip
                                                key={cid}
                                                size="small"
                                                variant="outlined"
                                                icon={<PlayArrowIcon sx={{ fontSize: 14, color: `${answerColors.ink} !important` }} />}
                                                label={`${when} call · ${m.start != null ? formatDuration(m.start) : 'open'}`}
                                                onClick={() => openAt(hit.call.id, m.start)}
                                                sx={{ borderColor: answerColors.border, color: answerColors.ink, backgroundColor: answerColors.chipBg, fontWeight: 600, '&:hover': { backgroundColor: answerColors.chipBg, filter: 'brightness(0.96)' } }}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: answerColors.muted }}>
                                {result.answer.found
                                    ? `Answer written from the ${result.hits.reduce((n, h) => n + h.matches.length, 0)} moments below only · nothing outside these calls was used · answers in the language of the question`
                                    : `${result.searched_calls} calls searched · the closest moments are shown below in case the wording was different`}
                            </Typography>
                        </Box>
                    )}
                    {result.hits.map((h) => (
                        <Box key={h.call.id} sx={{ p: 1.25, mb: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }} onClick={() => openAt(h.call.id, h.matches[0]?.start)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{personName(h.call.admin) || 'Admin'} → {whomText(h.call)}</Typography>
                                <Typography variant="caption" color="text.secondary">{dayjs(h.call.started_at || h.call.createdAt).format('MMM D, YYYY h:mm A')} · #{h.call.id}</Typography>
                                <Chip size="small" variant="outlined" color={statusColor(h.call.status)} label={`${CALL_STATUS_TEXT[h.call.status] || h.call.status}${h.call.duration_seconds != null ? ` · ${formatDuration(h.call.duration_seconds)}` : ''}`} />
                            </Box>
                            {h.matches.map((m) => <Moment key={m.chunk_id} m={m} terms={queryTerms(result.q)} markSx={markSx} contactName={contactName} onPlay={(sec) => openAt(h.call.id, sec)} />)}
                        </Box>
                    ))}
                </Box>
            ) : (
            <>
            {!loading && !error && calls.length === 0 && <Typography variant="body2" color="text.secondary">{`No calls with this ${kind} yet.`}</Typography>}
            {!loading && exact && term && exactCalls.length === 0 && calls.length > 0 && (
                <Typography variant="body2" color="text.secondary">No call mentions “{term}”. Turn off Exact words to search by meaning.</Typography>
            )}
            {exactCalls.map((c) => { const openable = canOpenCall(voice.me, c); const turns = exact && term ? matchingTurns(c, term) : []; return (
                <React.Fragment key={c.id}>
                <Box
                    onClick={openable ? () => openAt(c.id, null) : undefined}
                    title={openable ? undefined : 'Placed by another admin — only they, or an admin listed in VOICE_ADMIN_EMAILS, can open it'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 0.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', cursor: openable ? 'pointer' : 'default', opacity: openable ? 1 : 0.75, '&:hover': openable ? { borderColor: 'primary.main' } : undefined }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                            {c.started_at ? dayjs(c.started_at).format('MMM D, YYYY h:mm A') : dayjs(c.createdAt).format('MMM D, YYYY h:mm A')}
                            {' · '}{personName(c.admin) || 'Admin'} → {kind === 'student' && c.student && c.student.uuid !== person.uuid
                                ? `${whomText(c)} · filed here too` // this student is an "also file under" of a sibling's call
                                : (LABEL_TEXT[c.contact_label] || formatPhone(c.to_number))}
                        </Typography>
                        {(c.summary || c.notes) && <Typography variant="caption" color="text.secondary" noWrap display="block">{(c.summary || '').split('\n')[0] || c.notes}</Typography>}
                    </Box>
                    <Chip size="small" variant="outlined" color={statusColor(c.status)} label={`${CALL_STATUS_TEXT[c.status] || c.status}${c.duration_seconds != null ? ` · ${formatDuration(c.duration_seconds)}` : ''}`} />
                    <Chip size="small" variant="outlined" color={TRANSCRIPT_COLOR[c.transcript_status] || 'default'} label={TRANSCRIPT_TEXT[c.transcript_status] || c.transcript_status} />
                </Box>
                {turns.length > 0 && openable && (
                    <Box sx={{ px: 1.5, pb: 1.5, mt: -0.25, mb: 0.5 }}>
                        {turns.map((seg, i) => <Moment key={i} m={{ speaker: seg.speaker, start: seg.start, text: seg.text }} terms={term} markSx={markSx} contactName={contactName} onPlay={(sec) => openAt(c.id, sec)} />)}
                    </Box>
                )}
                </React.Fragment>
            ); })}
            </>
            )}
            <CallDetailDialog callId={openId} open={openId !== null} onClose={() => { setOpenId(null); setOpenSeek(null); }} onUpdated={load} initialSeek={openSeek} />
        </Box>
    );
};

export default CallHistoryTab;
